import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, CalendarDays, Camera, ChevronLeft, ChevronRight, Printer, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import BookingSection from "@/components/BookingSection";

const FOTOS = [
  {
    "src": "/marketing-photos/portrait-park-fit-site.svg",
    "alt": "Smiling portrait in a park wearing a blue cap",
    "label": "PORTRAITS",
    "framed": false,
    "wide": false
  },
  {
    "src": "/marketing-photos/editorial-portrait.svg",
    "alt": "Editorial portrait in a black gown beside white columns",
    "label": "EDITORIAL",
    "framed": false,
    "wide": false
  },
  {
    "src": "/marketing-photos/celebration-portrait.svg",
    "alt": "Celebration portrait with pink roses and a champagne bottle",
    "label": "CELEBRATIONS",
    "framed": false,
    "wide": false
  },
  {
    "src": "/marketing-photos/lake-portrait.svg",
    "alt": "Lakeside portrait on a floating dock surrounded by blue water",
    "label": "LIFESTYLE",
    "framed": false,
    "wide": false
  },
  {
    "src": "/marketing-photos/instant-teal-fit-site.svg",
    "alt": "Full-length portrait in a sparkling teal dress",
    "label": "INSTANT FOTOS",
    "framed": true,
    "wide": false
  },
  {
    "src": "/marketing-photos/keepsakes-fit-site.svg?v=hand-20261002",
    "alt": "Polaroid keepsake held in hand at a Pole & Polaroids event",
    "label": "KEEPSAKES",
    "showFullPhoto": true,
    "framed": false,
    "wide": false
  },
  {
    "src": "/marketing-photos/fashion-night-fit-site.svg",
    "alt": "Nighttime fashion portrait in sunglasses and a black lace dress",
    "label": "FASHION",
    "framed": false,
    "wide": false
  },
  {
    "src": "/marketing-photos/nightlife-red-fit-site.svg",
    "alt": "Friends at a red-lit club event",
    "label": "NIGHTLIFE",
    "framed": true,
    "wide": true
  },
  {
    "src": "/marketing-photos/street-style-fit-site.svg",
    "alt": "Instant-print street-style portrait against a red brick wall",
    "label": "STREET STYLE",
    "framed": true,
    "wide": false
  },
  {
    "src": "/marketing-photos/events-red-costume.webp",
    "alt": "New Orleans event portrait in a vibrant red feathered costume",
    "label": "EVENTS",
    "framed": true,
    "wide": true
  },
  {
    "src": "/marketing-photos/festival-family.webp",
    "alt": "Family and friends celebrating at a New Orleans festival",
    "label": "FESTIVAL FOTOS",
    "framed": true,
    "wide": true
  }
];

const services = [
  [Camera, "Event Photography", "Candid, editorial and flash-forward event coverage built around the energy of the room.", "/services/event-photography-new-orleans"],
  [Sparkles, "Foto Booth", "A hosted guest-first photo experience with digital delivery and optional instant keepsakes.", "/services/foto-booth-new-orleans"],
  [Camera, "Real Estate Media", "Polished listing photos plus social-ready property content for agents, owners and property managers.", "/services/real-estate-media-new-orleans"],
  [Camera, "Food + Restaurant", "Menu, cocktail, interior, staff and social content for restaurants, bars and culinary brands.", "/services/restaurant-food-photography-new-orleans"],
  [Camera, "Airbnb + Hospitality", "Property, detail and lifestyle visuals built to help guests understand the stay before they book.", "/services/hospitality-airbnb-media-new-orleans"],
  [Sparkles, "Business Content", "Photo and video libraries for websites, ads, social media and monthly content retainers.", "/services/business-content-new-orleans"],
  [Sparkles, "Event Recap Video", "Cinematic recap edits and vertical social clips that keep the event moving after it ends.", "/services/event-video-new-orleans"],
  [Printer, "Instant Keepsakes", "Physical photographs made during the event so the memory leaves with your guests.", "/services/instant-photo-printing-new-orleans"],
  [Camera, "Open Bar for Photos", "Roaming event coverage paired with physical guest prints throughout the celebration.", "/services/open-bar-for-photos"],
];

const BOOKING_URL = "https://fotosonthegeaux.setmore.com/";

const calendarHolds = [
  { start: "2026-10-17", end: "2026-10-25", kind: "blocked", label: "Production travel" },
  { start: "2026-12-30", end: "2027-01-10", kind: "blocked", label: "Production travel" },
  { start: "2027-03-06", end: "2027-03-06", kind: "blocked", label: "Unavailable" },
  { start: "2027-03-08", end: "2027-03-08", kind: "blocked", label: "Private hold" },
  { start: "2027-03-15", end: "2027-03-21", kind: "blocked", label: "Production travel" },
];

const publicDates = [
  { date: "OCT 17–25", title: "Production Travel", meta: "Blackout dates" },
  { date: "DEC 30–JAN 10", title: "Production Travel", meta: "Blackout dates" },
  { date: "MAR 06", title: "Unavailable", meta: "Date blocked" },
  { date: "MAR 15–21", title: "Production Travel", meta: "Blackout dates" },
];

const isoDate = date => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

function AvailabilityCalendar() {
  const today = new Date();
  const firstBookableMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastBookableMonth = new Date(today.getFullYear(), today.getMonth() + 11, 1);
  const [month, setMonth] = useState(firstBookableMonth);
  const monthName = month.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const firstOffset = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7;
  const dayCount = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Array.from({ length: firstOffset + dayCount }, (_, index) => index < firstOffset ? null : index - firstOffset + 1);
  const prevDisabled = month <= firstBookableMonth;
  const nextDisabled = month >= lastBookableMonth;
  const moveMonth = delta => setMonth(current => new Date(current.getFullYear(), current.getMonth() + delta, 1));

  return <section id="availability" className="border-y border-white/10 bg-[#080808] px-5 py-24 sm:px-8 md:py-32">
    <div className="mx-auto max-w-7xl">
      <div className="grid gap-8 lg:grid-cols-[1.1fr_.9fr] lg:items-end">
        <div>
          <p className="font-accent text-[9px] tracking-[.35em] text-[#b99870]">LIVE AVAILABILITY</p>
          <h2 className="mt-4 max-w-3xl font-display text-5xl font-black leading-[.9] sm:text-7xl">Catch an open date.</h2>
        </div>
        <p className="max-w-xl text-white/55 lg:justify-self-end">A preview of production holds and booking windows. Select a service to see exact live times and secure your date.</p>
      </div>

      <div className="mt-14 grid gap-6 lg:grid-cols-[1.15fr_.85fr]">
        <div className="border border-white/15 bg-black p-4 shadow-[0_30px_90px_rgba(0,0,0,.5)] sm:p-7">
          <div className="flex items-center justify-between border-b border-white/10 pb-5">
            <div>
              <p className="font-accent text-[8px] tracking-[.28em] text-[#b99870]">BOOKING PREVIEW</p>
              <h3 className="mt-1 font-display text-3xl font-bold">{monthName}</h3>
            </div>
            <div className="flex gap-2">
              <button type="button" aria-label="Previous month" disabled={prevDisabled} onClick={() => moveMonth(-1)} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white disabled:opacity-20"><ChevronLeft size={16}/></button>
              <button type="button" aria-label="Next month" disabled={nextDisabled} onClick={() => moveMonth(1)} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white disabled:opacity-20"><ChevronRight size={16}/></button>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-7 gap-1 text-center font-accent text-[8px] tracking-[.16em] text-white/35">
            {["MON","TUE","WED","THU","FRI","SAT","SUN"].map(day => <span key={day} className="py-2">{day}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, index) => {
              if (!day) return <span key={`blank-${index}`} className="aspect-square" />;
              const date = new Date(month.getFullYear(), month.getMonth(), day);
              const key = isoDate(date);
              const hold = calendarHolds.find(item => key >= item.start && key <= item.end);
              const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const state = isPast ? "past" : hold?.kind || "open";
              const styles = state === "blocked"
                ? "border-white/10 bg-white/[.035] text-white/25 line-through"
                : state === "past"
                  ? "border-transparent text-white/15"
                  : "border-white/10 bg-white/[.02] text-white/75 hover:border-[#b99870]/70";
              return <div key={key} title={hold?.label || (isPast ? "Past date" : "Open booking window")} className={`relative flex aspect-square items-center justify-center border text-xs transition-colors sm:text-sm ${styles}`}>
                {day}
                {state === "open" && <span className="absolute bottom-1.5 h-1 w-1 rounded-full bg-[#b99870]"/>}
              </div>;
            })}
          </div>
          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 font-accent text-[8px] tracking-[.15em] text-white/45">
            <span className="flex items-center gap-2"><i className="h-2 w-2 rounded-full bg-[#b99870]"/>OPEN WINDOW</span>
            <span className="flex items-center gap-2"><i className="h-2 w-2 border border-white/20 bg-white/[.04]"/>UNAVAILABLE</span>
          </div>
        </div>

        <aside className="border border-white/15 bg-[#0c0c0c] p-6 sm:p-8">
          <p className="font-accent text-[9px] tracking-[.3em] text-[#b99870]">ON THE CALENDAR</p>
          <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
            {publicDates.map(item => <div key={item.date} className="grid grid-cols-[92px_1fr] gap-4 py-5">
              <span className="font-accent text-[9px] tracking-[.14em] text-[#b99870]">{item.date}</span>
              <div><h3 className="font-display text-xl font-bold">{item.title}</h3><p className="mt-1 text-xs text-white/40">{item.meta}</p></div>
            </div>)}
          </div>
          <p className="mt-6 text-sm leading-6 text-white/45">Dates marked unavailable are reserved for confirmed events, production, travel or delivery. Availability is not guaranteed until your booking is confirmed.</p>
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#f2eee4] px-6 py-3 font-accent text-[9px] tracking-[.2em] text-black">VIEW LIVE TIMES <ArrowRight size={14}/></a>
        </aside>
      </div>
    </div>
  </section>;
}

function Print({ foto, smx, smy, depth=1, rotate=0, className="", delay=0, dim=false }) {
  const x = useTransform(smx, v => v * depth * 75);
  const y = useTransform(smy, v => v * depth * 48);
  return <motion.figure className={`absolute pointer-events-none ${className}`} style={{x,y,z:depth*60,transformStyle:"preserve-3d"}} initial={{opacity:0,scale:.88}} animate={{opacity:dim?.5:1,scale:1}} transition={{duration:1,delay}}>
    <motion.div animate={{y:[0,-8-depth*3,0]}} transition={{duration:6+delay,repeat:Infinity,ease:"easeInOut"}} style={{transform:`rotate(${rotate}deg)`}} className="bg-[#f2eee4] p-2 pb-7 shadow-[0_30px_80px_rgba(0,0,0,.9)] ring-1 ring-white/20">
      <img src={foto.src} alt={foto.alt} decoding="async" className={`aspect-[3/4] w-full bg-[#f2eee4] ${foto.showFullPhoto ? "object-contain" : "object-cover object-center"}`} />
      <figcaption className="pt-2 text-center font-accent text-[9px] font-semibold tracking-[.14em] text-black/75 sm:text-[10px]">{foto.label}</figcaption>
    </motion.div>
  </motion.figure>;
}

function Hero(){
  const mx=useMotionValue(0), my=useMotionValue(0);
  const smx=useSpring(mx,{stiffness:55,damping:20}), smy=useSpring(my,{stiffness:55,damping:20});
  const move=e=>{const r=e.currentTarget.getBoundingClientRect();mx.set((e.clientX-r.left)/r.width-.5);my.set((e.clientY-r.top)/r.height-.5)};
  return <section onPointerMove={move} onPointerLeave={()=>{mx.set(0);my.set(0)}} className="relative min-h-[100svh] overflow-hidden bg-[#050505]" style={{perspective:"1500px"}}>
    <div className="pointer-events-none absolute inset-0 opacity-70 bg-[radial-gradient(circle_at_50%_42%,rgba(188,141,86,.16),transparent_22%),radial-gradient(circle_at_20%_75%,rgba(255,255,255,.05),transparent_28%)]" />
    <Print foto={FOTOS[0]} smx={smx} smy={smy} depth={.7} rotate={-8} delay={.05} className="left-[-8%] top-[15%] w-32 sm:left-[2%] sm:w-48 lg:left-[5%] lg:w-56" />
    <Print foto={FOTOS[1]} smx={smx} smy={smy} depth={1.15} rotate={7} delay={.12} className="right-[-7%] top-[13%] w-28 sm:right-[3%] sm:w-44 lg:right-[8%] lg:w-48" />
    <Print foto={FOTOS[2]} smx={smx} smy={smy} depth={1.3} rotate={5} delay={.2} className="bottom-[5%] left-[-7%] w-28 sm:left-[7%] sm:w-44 lg:left-[13%] lg:w-52" />
    <Print foto={FOTOS[3]} smx={smx} smy={smy} depth={.85} rotate={-6} delay={.28} className="bottom-[7%] right-[-8%] w-32 sm:right-[6%] sm:w-48 lg:right-[13%] lg:w-56" />
    <Print foto={FOTOS[4]} smx={smx} smy={smy} depth={.55} rotate={11} delay={.35} className="hidden lg:block left-[23%] top-[5%] w-40" />
    <Print foto={FOTOS[5]} smx={smx} smy={smy} depth={.3} rotate={-11} delay={.4} dim className="hidden lg:block right-[25%] bottom-[5%] w-28" />
    <div className="relative z-20 mx-auto flex min-h-[100svh] w-full max-w-5xl min-w-0 flex-col items-center justify-center px-4 py-24 text-center sm:px-6 sm:py-28">
      <p className="font-accent text-[9px] tracking-[.34em] text-[#c8aa82] sm:text-[10px]">NEW ORLEANS · PHOTO · VIDEO · EVENT EXPERIENCES</p>
      <p className="mt-5 font-display italic text-lg text-[#b99870] sm:text-2xl">Visual media built to sell the experience.</p>
      <h1 className="mt-2 w-full max-w-full font-display text-[clamp(4rem,17vw,8rem)] font-black leading-[.78] tracking-[-.065em] text-[#f5f0e7] sm:text-[clamp(5rem,12vw,8rem)]">FOTOS<span className="mt-4 block text-[.39em] tracking-[.24em]">ON THE GEAUX</span></h1>
      <p className="mt-8 max-w-2xl text-xs uppercase leading-7 tracking-[.18em] text-white/55 sm:text-sm">Properties · food · brands · events · people · physical memories</p>
      <div className="relative z-30 mt-8 flex flex-wrap justify-center gap-3">
        <a href="#book" className="inline-flex items-center gap-2 rounded-full bg-[#f2eee4] px-6 py-3 font-accent text-[10px] tracking-[.2em] text-black">BOOK FOTOS <CalendarDays size={14}/></a>
        <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/50 px-6 py-3 font-accent text-[10px] tracking-[.2em]">EXPLORE SERVICES <ArrowRight size={14}/></Link>
        <Link to="/services/foto-booth-new-orleans" className="rounded-full border border-white/25 bg-black/50 px-6 py-3 font-accent text-[10px] tracking-[.2em]">FOTO BOOTH</Link>
        <Link to="/pole-and-polaroids" className="inline-flex items-center gap-2 rounded-full border border-[#E32636]/60 bg-[#E32636]/10 px-6 py-3 font-accent text-[10px] tracking-[.2em] text-[#f5f0e7] transition hover:border-[#E32636] hover:bg-[#E32636]/20">POLE &amp; POLAROIDS <ArrowRight size={14}/></Link>
      </div>
    </div>
  </section>
}

export default function CompanyHome(){return <div className="company-page min-h-screen bg-black text-[#f5f0e7] selection:bg-white selection:text-black">
  <Seo title="Fotos on the Geaux | New Orleans Photography, Video & Foto Booth" description="Fotos on the Geaux creates premium photography, video, business content, real estate media, food photography, hospitality visuals, Foto Booth experiences and instant keepsakes in New Orleans." path="/" />
  <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-xl">
    <div className="mx-auto flex max-w-7xl min-w-0 items-center justify-between gap-3 px-4 py-4 sm:px-5">
      <Link to="/" className="font-accent text-xs tracking-[.24em] sm:text-sm">FOTOS ON THE GEAUX</Link>
      <nav className="hidden gap-7 font-accent text-[9px] tracking-[.2em] text-white/55 md:flex">
        <a href="#gallery">PORTFOLIO</a><Link to="/gallery">EVENT PHOTOS</Link><Link to="/services">SERVICES</Link><Link to="/pole-and-polaroids" className="whitespace-nowrap text-[#E32636] transition hover:text-white">POLE &amp; POLAROIDS</Link><Link to="/insights">GUIDES</Link><a href="#availability">CALENDAR</a><a href="#book">BOOK</a>
      </nav>
      <div className="flex items-center gap-2">
        <a href="#book" className="rounded-full bg-[#f2eee4] px-4 py-2 font-accent text-[8px] tracking-[.18em] text-black sm:text-[9px]">BOOK</a>
        <Link to="/services/foto-booth-new-orleans" className="rounded-full border border-white/25 px-4 py-2 font-accent text-[8px] tracking-[.18em] sm:text-[9px]">FOTO BOOTH</Link>
      </div>
    </div>
  </header>
  <main>
    <Hero/>

    <section id="gallery" className="border-y border-white/10 px-5 py-24 sm:px-8 md:py-32">
      <div className="mx-auto max-w-7xl">
        <p className="font-accent text-[9px] tracking-[.35em] text-[#b99870]">THE WORK</p>
        <div className="mt-4 grid gap-8 lg:grid-cols-2 lg:items-end">
          <h2 className="font-display text-5xl font-black leading-[.9] sm:text-7xl">Content people can feel.</h2>
          <p className="max-w-xl text-white/55 lg:justify-self-end">Portraits, personal style, celebrations and nights out—photographed by Fotos on the Geaux, with memories you can keep.</p>
        </div>
        <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-7">
          {FOTOS.map((f,i)=><motion.figure key={f.src} whileHover={{y:-10,rotate:i%2?1:-1}} className={`${f.wide?'md:col-span-2':''} ${i%2?'rotate-[2deg]':'rotate-[-2deg]'}`}>
            <div className={`float-img bg-[#f2eee4] ${f.label === "KEEPSAKES" ? "px-1.5 pt-2.5 pb-9" : "p-2.5 pb-9"} shadow-[0_25px_70px_rgba(0,0,0,.85)]`}>
              <img src={f.src} alt={f.alt} loading="lazy" decoding="async" className={`w-full bg-[#f2eee4] ${f.showFullPhoto ? "object-contain object-top" : "object-cover"} ${f.wide?'aspect-[4/3]':'aspect-[3/4]'}`} style={{objectPosition: f.showFullPhoto ? "50% 0%" : f.label === "FESTIVAL FOTOS" ? "50% 30%" : f.label === "EVENTS" ? "50% 34%" : "center"}}/>
              <p className="pt-3 text-center font-accent text-[10px] font-semibold tracking-[.14em] text-black/75 sm:text-xs">{f.label}</p>
            </div>
          </motion.figure>)}
        </div>
      </div>
    </section>

    <section id="services" className="px-5 py-24 sm:px-8 md:py-32">
      <div className="mx-auto max-w-7xl">
        <p className="font-accent text-[9px] tracking-[.35em] text-[#b99870]">WHAT WE DO</p>
        <h2 className="mt-4 max-w-4xl font-display text-5xl font-black leading-[.9] sm:text-7xl">Full-service visual media for businesses that need content that works.</h2>
        <p className="mt-6 max-w-3xl text-base leading-7 text-white/55">Book one shoot or build an ongoing content system. We create photography, video, guest experiences and physical keepsakes for New Orleans businesses, properties and events.</p>
        <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
          {services.map(([Icon,title,copy,href],i)=><Link key={title} to={href} className="group grid gap-4 py-8 md:grid-cols-[50px_1fr_1fr_30px] md:items-center">
            <span className="font-accent text-[9px] text-white/30">{String(i+1).padStart(2,"0")}</span>
            <div className="flex items-center gap-4"><Icon size={20}/><h3 className="font-display text-3xl font-bold">{title}</h3></div>
            <p className="text-sm leading-6 text-white/45">{copy}</p>
            <ArrowRight size={17} className="text-white/35 group-hover:text-white"/>
          </Link>)}
        </div>
        <div className="mt-10 flex flex-wrap gap-3"><a href="#book" className="inline-flex items-center gap-2 rounded-full bg-[#f2eee4] px-7 py-3 font-accent text-[10px] tracking-[.2em] text-black">CHECK YOUR DATE <CalendarDays size={14}/></a><Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3 font-accent text-[10px] tracking-[.2em]">ALL SERVICES <ArrowRight size={14}/></Link></div>
      </div>
    </section>

    <section className="border-y border-white/10 bg-[#0a0a0a] px-5 py-20 sm:px-8">
      <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-[.75fr_1.25fr] md:items-center">
        <div>
          <p className="font-accent text-[9px] tracking-[.35em] text-[#b99870]">COMING NEXT</p>
          <h2 className="mt-4 font-display text-4xl font-black leading-[.95] sm:text-6xl">Drone + aerial media.</h2>
        </div>
        <div>
          <p className="max-w-2xl text-base leading-7 text-white/55">Aerial photo and video is part of the long-term Fotos on the Geaux media offering, but it is not being sold as a current service yet. Additional flight practice, production testing and workflow development come first. Target launch: 2027.</p>
          <p className="mt-4 text-sm text-white/35">Until launch, current real estate, hospitality and commercial packages are built around ground-based photography and video.</p>
        </div>
      </div>
    </section>

    <AvailabilityCalendar />
    <BookingSection />

    <section id="about" className="border-t border-white/10 px-5 py-20 text-center">
      <div className="mx-auto max-w-3xl">
        <p className="font-display text-4xl font-black sm:text-6xl">More than a photographer.</p>
        <p className="mx-auto mt-5 max-w-2xl text-white/50">Fotos on the Geaux is a New Orleans visual media partner for businesses, properties, food, brands and events—built around strong images, useful content and memorable guest experiences.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href="#book" className="inline-flex rounded-full bg-[#f2eee4] px-7 py-3 font-accent text-[10px] tracking-[.2em] text-black">BOOK FOTOS</a>
          <Link to="/pole-and-polaroids" className="inline-flex rounded-full border border-white/20 px-7 py-3 font-accent text-[10px] tracking-[.2em]">VIEW POLE & POLAROIDS</Link>
        </div>
      </div>
    </section>
  </main>
  <footer className="border-t border-white/10 px-5 py-10 text-[10px] tracking-[.15em] text-white/30">
    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4"><span>© 2026 FOTOS ON THE GEAUX℠</span><nav className="flex flex-wrap gap-4"><Link to="/about-fotos-on-the-geaux">ABOUT</Link><Link to="/services">SERVICES</Link><Link to="/insights">GUIDES</Link><Link to="/pole-and-polaroids">POLE & POLAROIDS</Link><Link to="/book">BOOK</Link></nav><span>NEW ORLEANS, LOUISIANA</span></div>
  </footer>
</div>}
